import type { ProspectResponse } from "../types/api";

/**
 * Pin favorited prospects to the top of any list, ordered by most-recently
 * favorited first. Among favorites (and among non-favorites), the optional
 * `fallback` comparator preserves the caller's existing order.
 *
 * Invariant from the API: `favoritedAt` is non-null iff `isFavorite` is true.
 * Ties within the favorites group resolve by prospect id ascending so the
 * order is stable when timestamps collide (backfilled rows, rapid toggles).
 */
export const pinFavoritesFirst = <T extends ProspectResponse>(
  prospects: T[],
  fallback?: (a: T, b: T) => number
): T[] => {
  const sorted = [...prospects];
  sorted.sort((a, b) => {
    if (a.isFavorite !== b.isFavorite) {
      return a.isFavorite ? -1 : 1;
    }

    if (a.isFavorite && b.isFavorite) {
      const aTime = a.favoritedAt ? Date.parse(a.favoritedAt) : 0;
      const bTime = b.favoritedAt ? Date.parse(b.favoritedAt) : 0;
      if (aTime !== bTime) {
        return bTime - aTime;
      }
      return a.id.localeCompare(b.id);
    }

    return fallback ? fallback(a, b) : 0;
  });
  return sorted;
};
